import {
  Component,
  Input,
  OnChanges,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  HostBinding,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { merge, chunk, max, isEqual } from 'lodash';
import { from } from 'rxjs';
import { map } from 'rxjs/operators';

import {
  ColorService,
  DEFAULT_EXCLUDE_FALSY_TOOGLE,
  Metric,
  Result,
  BenchmarkService,
  DatesService,
  Query,
  DateRangeValue,
  DynamicDateRange,
  Series,
  Row,
  MessageService,
  EMPTY_FALSY_ERROR,
  TrackingService,
  WidgetQueueProxyService,
} from '@hub-components';
import { BenchmarkSelection } from 'app/hub-components/components/benchmarks/benchmarks.component';
import { DEFAULT_TABLE_TEXT_FONT_SIZE, DEFAULT_RESULTS_PER_PAGE, DEFAULT_COMPARE_TO, DEFAULT_SHOW_COLUMN_GRAND_TOTALS, DEFAULT_SHOW_ROW_GRAND_TOTALS, DEFAULT_SHOW_COLUMN_ALL_PAGES_TOTALS } from '../../../../constants';
import { MetricTotal } from '../../interfaces/metric-total.interface';
import { WidgetStyle, WidgetStyleOptions } from '../../interfaces/widget-style.interface';
import { ChartOptions } from '../chart-options';
type MetricComparison = 'green_increase' | 'red_increase';

@Component({
  selector: 'table-widget',
  templateUrl: 'table-widget.component.html',
  styleUrls: ['table-widget.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})

export class TableWidgetComponent implements OnChanges, OnInit {
  @HostBinding('style.backgroundColor') backgroundColor: string;

  @Input() result: Result;
  @Input() metrics: Metric[];
  @Input() style: WidgetStyle;
  @Input() chartOptions: ChartOptions = {};
  @Input() dashboardInterval: DynamicDateRange;

  dateFormat = { pattern: 'MMM d, yyyy', timezone: 'UTC' };

  static supportedOptions = [
    {
      bindTo: 'rollup',
      label: 'Sum by',
      type: 'rollupDropdown',
    },
    {
      bindTo: 'transpose',
      label: 'Show dates as',
      type: 'transpose',
    },
    {
      bindTo: 'limit',
      label: 'Number of results',
      type: 'limitDropdown',
    },
    {
      bindTo: 'resultsPerPage',
      label: 'Results per page',
      type: 'resultsPerPageDropdown',
    },
    {
      bindTo: 'dataOptions',
      label: 'Data',
      type: 'dataOptions',
      options: [
        {
          bindTo: 'excludeFalsy',
          label: 'Exclude N/A and 0 values by row',
        }, {
          bindTo: 'showColumnGrandTotals',
        }, {
          bindTo: 'showColumnAllPagesTotals',
        }, {
          bindTo: 'showRowGrandTotals',
        },
      ],
    },
    {
      bindTo: 'heatmap',
      label: 'Heatmap',
      type: 'checkbox',
    },
    {
      bindTo: 'title',
      label: 'Title',
      type: 'textFormat',
      options: [
        {
          bindTo: 'text',
        }, {
          bindTo: 'align',
        }, {
          bindTo: 'size',
        },
      ],
    },
    {
      bindTo: 'footer',
      label: 'Footer',
      type: 'footerCheckbox',
      options: [
        {
          bindTo: 'datasetInfo',
        },
        {
          bindTo: 'dateRange',
        },
      ],
    },
    {
      bindTo: 'tableText',
      label: 'Text',
      type: 'textFormat',
      options: [
        {
          bindTo: 'size',
        },
      ],
    },
    {
      bindTo: 'dateRange',
      label: 'Date range',
      type: 'dateRange',
      options: [
        {
          bindTo: 'enabled',
        }, {
          bindTo: 'value',
        },
      ],
    },
    {
      bindTo: 'compareTo',
      label: 'Compare To',
      type: 'compareToDropdown',
    },
  ];

  static supportedStyle: WidgetStyleOptions[] = [
    {
      bindTo: 'backgroundColor',
      label: 'Background Color',
    },
    {
      bindTo: 'textColor',
      label: 'Text Color',
    },
    {
      bindTo: 'borderWidth',
      label: 'Thickness',
    },
    {
      bindTo: 'borderColor',
      label: 'Color',
    },
    {
      bindTo: 'shadow',
      label: 'Size',
    },
    {
      bindTo: 'shadowColor',
      label: 'Color',
    },
    {
      bindTo: 'shadowOpacity',
      label: 'Opacity',
    },
    {
      bindTo: 'borderRadius',
      label: 'Corner Radius',
    },
  ];

  static supportedRollups = ['day', 'week', 'month', 'quarter', 'year', 'total'];
  textColor: string;
  title: { [key: string]: string | number };
  colorScale: any;
  textSize: number = DEFAULT_TABLE_TEXT_FONT_SIZE;
  footer: any;
  public footerData: any;

  maxValues: { [key: string]: number };
  uniqueDates: any;


  public pages: any;
  public resultSeries: Series[];
  public uniqueSlices: any;
  public datesAsRowsTotals: MetricTotal[] = [];
  public datesAsColumnsColTotals: MetricTotal[] = [];
  public datesAsColumnsRowTotals: MetricTotal[][] = [];
  public datesAsColumnsTotalOfTotals: MetricTotal[] = [];
  public showColumnGrandTotals = DEFAULT_SHOW_COLUMN_GRAND_TOTALS;
  public showRowGrandTotals = DEFAULT_SHOW_ROW_GRAND_TOTALS;
  public showColumnAllPagesTotals = DEFAULT_SHOW_COLUMN_ALL_PAGES_TOTALS;
  public emptyTableCells: String[];
  public updateResult: boolean = true;
  public initialPageNum: number = 0;
  private _uniqueSlices: any;
  private _filteredUniqueSlices: any;
  private _resultData: Series[];
  private _filteredResultData: Series[];
  private _resultSeries: Series[];
  private _pages: any;
  private _filteredPages: any;
  private _excludeFalsy = DEFAULT_EXCLUDE_FALSY_TOOGLE;
  private isFilterEmpty = false;
  private _firstLoad: boolean = true;
  resultsPerPage = DEFAULT_RESULTS_PER_PAGE;
  compareTo: BenchmarkSelection = DEFAULT_COMPARE_TO;
  currentPageNum = 0;


  datasetDisplayName: string;

  benchmarkPages: any;
  loadingBenchmarks: boolean = false;

  constructor(
    colorService: ColorService,
    private changeDetectorRef: ChangeDetectorRef,
    private benchmarkService: BenchmarkService,
    private datesService: DatesService,
    private messageService: MessageService,
    private trackingService: TrackingService,
    private widgetQueueProxy: WidgetQueueProxyService,
  ) {
    this.colorScale = colorService.getScale(['#e57c76', '#f2b8a3', '#fff3cf', '#aed7ae', '#5abb8c']);
  }

  ngOnInit(): void {
    if (this.chartOptions?.initialPageNum) {
      this.currentPageNum = this.chartOptions.initialPageNum;
      this.initialPageNum = this.chartOptions.initialPageNum;
    }
  }

  ngOnChanges(changes) {
    if (this.style) {
      this.textColor = this.style.textColor;
      this.backgroundColor = this.style.backgroundColor;
    }

    if (this.result?.series) {
      this._resultSeries = this.result.series;
      this._resultData = this.getResultsSeriesData(this._resultSeries, false);
      this._filteredResultData = this.getResultsSeriesData(this._resultSeries, true);
      this.isFilterEmpty = this._filteredResultData.length === 0 && this._resultSeries.length !== 0;
      if (this.isFilterEmpty && this._excludeFalsy) {
        this.messageService.info(EMPTY_FALSY_ERROR);
      }
      this.updateResultSeriesData();
    }

    if (this.chartOptions) {
      this.addTracking(changes);

      this.footer = this.chartOptions.footer;
      this.footerData = {
        datasetInfo: this.result?.query?.schema?.displayName,
        dateRange: this.datesService.intervalDateRange(this.dashboardInterval, this.result?.query?.firstDayOfWeek ? Number(this.result.query.firstDayOfWeek) : undefined),
      };

      if (this.chartOptions.title) {
        this.title = this.chartOptions.title;
        this.title.align = this.chartOptions.title.align || 'center';
      }
      if (this.chartOptions.tableText) {
        this.textSize = this.chartOptions.tableText.size;
      }

      if (this.chartOptions.compareTo) {
        this.compareTo = {
          name: this.chartOptions.compareTo.name,
          value: this.chartOptions.compareTo.value,
          dateRange: this.chartOptions.compareTo.dateRange,
          query: this.chartOptions.compareTo.query,
        };
        if (this.result && this.result.query) {
          const dateRange = this.chartOptions.dateRange && this.chartOptions.dateRange.enabled ? this.chartOptions.dateRange.value : this.dashboardInterval;
          const { startDate, endDate } = this.datesService.parseValue(dateRange, this.result.query.firstDayOfWeek ? Number(this.result.query.firstDayOfWeek) : undefined);
          const dateRangeInvalid = !this.datesService.isValid(startDate, endDate);

          if (!dateRangeInvalid) {
            const benchmarkDates = this.getBenchmarkDates(dateRange);

            if (benchmarkDates) {
              this.loadingBenchmarks = true;
              const benchmarkBase = this.result.query.clone();
              benchmarkBase.interval = this.intervalFromDates(benchmarkDates);
              benchmarkBase.auth = this.chartOptions.compareTo.query && this.chartOptions.compareTo.query['auth'];
              this.chartOptions.compareTo.query = merge({}, benchmarkBase.asJson(), { auth: benchmarkBase.auth });
              const benchmarkResults = this.runQuery(benchmarkBase);
              this.updateBenchmarkPages(benchmarkResults);
            }
          }
        }
      }

      if (this.chartOptions.dataOptions) {
        const excludeFalsyHasChanged = this.hasNewChanges(changes.options?.previousValue?.dataOptions.excludeFalsy, changes.options?.currentValue?.dataOptions.excludeFalsy);
        const showColumnGrandTotalsHasChanged = this.hasNewChanges(changes.options?.previousValue?.dataOptions.showColumnGrandTotals, changes.options?.currentValue?.dataOptions.showColumnGrandTotals);
        const showRowGrandTotalsHasChanged = this.hasNewChanges(changes.options?.previousValue?.dataOptions.showRowGrandTotals, changes.options?.currentValue?.dataOptions.showRowGrandTotals);
        const showColumnAllPagesTotalsHasChanged = this.hasNewChanges(changes.options?.previousValue?.dataOptions.showColumnAllPagesTotals, changes.options?.currentValue?.dataOptions.showColumnAllPagesTotals);

        if (excludeFalsyHasChanged) {
          if (!this._excludeFalsy && this.isFilterEmpty) {
            this.messageService.info(EMPTY_FALSY_ERROR);
          }
          this._excludeFalsy = this.chartOptions.dataOptions.excludeFalsy;
          this.updateSeriesData();
          this.updateSlicesData();
          this.updateResultSeriesData();
          this.resetToFirstPage();
        }

        if (showColumnGrandTotalsHasChanged) {
          this.showColumnGrandTotals = this.chartOptions.dataOptions.showColumnGrandTotals;
          this.resetToFirstPage();
        }

        if (showRowGrandTotalsHasChanged) {
          this.showRowGrandTotals = this.chartOptions.dataOptions.showRowGrandTotals;
          this.resetToFirstPage();
        }

        if (showColumnAllPagesTotalsHasChanged) {
          this.showColumnAllPagesTotals = this.chartOptions.dataOptions.showColumnAllPagesTotals;
          this.resetToFirstPage();
        }
      }

      if (this.chartOptions.resultsPerPage) {
        const resultsPerPageHasChanged = this.hasNewChanges(changes.options?.previousValue?.resultsPerPage, changes.options?.currentValue?.resultsPerPage);
        if (resultsPerPageHasChanged) {
          this.resultsPerPage = this.chartOptions.resultsPerPage.value;
          this.resetToFirstPage();
        }
      }
    }

    if (this.result?.rows?.length && this.chartOptions?.heatmap) {
      this.maxValues = {};
      for (const index in this.result.metrics) {
        const metricName = this.result.rows[0].metrics[index].name;
        const values = this.result.rows.map(row => row.metrics[index].value);
        const maxValue = max(values.filter(v => v !== null));
        this.maxValues[metricName] = maxValue;
      }
    }

    if (this.result) {
      this.datasetDisplayName = <string> this.result.query.schema.displayName;
      const transposeHasChanged = this.hasNewChanges(changes.options?.previousValue?.transpose, changes.options?.currentValue?.transpose);
      if (transposeHasChanged && !this._firstLoad) {
        this.resetToFirstPage();
      }
      this.chartOptions.transpose = this.chartOptions.transpose || 'rows';
      const uniqueDateRows = this.result.rows.slice(0, this.result.timestamps.length);
      this.uniqueDates = uniqueDateRows.map(row => row.date);

      if (this.result?.query?.rollup?.value === 'month') {
        this.dateFormat = { pattern: 'MMM yyyy', timezone: 'UTC' };
      } else if (this.result?.query?.rollup?.value === 'year') {
        this.dateFormat = { pattern: 'yyyy', timezone: 'UTC' };
      } else {
        this.dateFormat = { pattern: 'MMM d, yyyy', timezone: 'UTC' };
      }
    }

    if (this.result?.rows) {
      const resultHasChanged = this.hasNewChanges(changes.result?.previousValue, changes.result?.currentValue);
      if (resultHasChanged || this.updateResult) {
        this._pages = chunk(this.getRowsData(this.result.rows, false), this.resultsPerPage).map(group => group.filter(item => item !== null));
        this._filteredPages = chunk(this.getRowsData(this.result.rows, true), this.resultsPerPage).map(group => group.filter(item => item !== null));
        this._uniqueSlices = this.getSlicesData(false);
        this._filteredUniqueSlices = this.getSlicesData(true);
        this.updateSeriesData();
        this.updateSlicesData();
        this.updateResultSeriesData();

        if (this.showColumnGrandTotals || this.showRowGrandTotals) {
          this.calculateGrandTotals();
        }

        if (this.showColumnAllPagesTotals) {
          this.setEmptyTableCells();
        }

        this.updateResult = false;
        if (!this._firstLoad) {
          this.updateCurrentPageNum(0);
        }
        this._firstLoad = false;
      }
    }
  }

  private setEmptyTableCells() {
    this.emptyTableCells = [];
    this.result.dimensions.forEach(dimension => {
      this.emptyTableCells.push(dimension.name);
      if (dimension.properties) {
        dimension.properties.forEach(prop => {
          this.emptyTableCells.push(prop.name);
        });
      }
    });
  }

  private calculateGrandTotals(): void {
    this.setEmptyTableCells();
    this.datesAsColumnsRowTotals = [];
    this.datesAsColumnsColTotals = [];
    const datesAsRowsTotals = {};
    const startIndex = this.currentPageNum * this.resultsPerPage;
    const endIndex = startIndex + this.resultsPerPage;

    if (this.chartOptions?.transpose !== 'columns' && this.result?.query?.rollup?.value !== 'total') {
      const pageRows = this.pages[this.currentPageNum] || [];
      pageRows.forEach(row => {
        row.metrics.forEach(metric => {
          const format = metric.format?.['format'] || metric.format;
          const hide = format === 'percent' || format === 'date';
          if (!datesAsRowsTotals[metric.name]) {
            datesAsRowsTotals[metric.name] = { value: 0, format: metric.format, hide: hide };
          }
          datesAsRowsTotals[metric.name].value += metric.value || 0;
        });
      });
      this.datesAsRowsTotals = Object.values(datesAsRowsTotals || {});
    } else {
      const pageResultSeries = this.resultSeries.slice(startIndex, endIndex);
      pageResultSeries.forEach(serie => {
        let rowTotals = [];
        let index = 0;
        serie.metrics.forEach(metric => {
          const format = metric.format?.['format'] || metric.format;
          const hide = format === 'percent' || format === 'date';
          datesAsRowsTotals[metric.name] = { value: (datesAsRowsTotals[metric.name] && datesAsRowsTotals[metric.name].value || 0) + metric.aggregate, format: metric.format, hide: hide };
          rowTotals.push({ value: metric.aggregate, format: metric.format, hide: hide });
          metric.values.forEach(value => {
            if (this.datesAsColumnsColTotals[index]) {
              this.datesAsColumnsColTotals[index]['value'] += Number(value) || 0;
            } else {
              this.datesAsColumnsColTotals[index] = { value: Number(value) || 0, format: metric.format, hide: hide };
            }
            index++;
          });
        });
        this.datesAsColumnsRowTotals.push(rowTotals);
      });
      this.datesAsRowsTotals = Object.values(datesAsRowsTotals || {});
    }

    this.datesAsColumnsTotalOfTotals = [];
    this.datesAsColumnsRowTotals.forEach(rowTotal => {
      rowTotal.forEach((metric, index) => {
        if (this.datesAsColumnsTotalOfTotals[index]) {
          this.datesAsColumnsTotalOfTotals[index]['value'] += metric['value'] || 0;
        } else {
          this.datesAsColumnsTotalOfTotals[index] = { value: metric['value'] || 0, format: metric['format'], hide: metric['hide'] };
        }
      });
    });
  }

  private hasNewChanges(previousValue, newValue): boolean {
    return JSON.stringify(previousValue) !== JSON.stringify(newValue);
  }

  private resetToFirstPage(): void {
    this.updateResult = true;
    this.updateCurrentPageNum(0);
  }

  private getRowsData(resultRows: Row[], excludeFalsy: boolean): Row[] {
    return resultRows.filter(row => excludeFalsy ? row.metrics.some(metric => !!metric.value) : true);
  }

  private getSlicesData(excludeFalsy: boolean) {
    const resultData = this.result.rows.filter(row => excludeFalsy ? row.metrics.some(metric => !!metric.value) : true);
    const data = this.removeDuplicatedSlices(resultData.map(row => row.slices));
    return chunk(data, this.resultsPerPage).map(group => group.filter(item => item !== null));
  }

  private updateSlicesData() {
    if (this._excludeFalsy) {
      this.uniqueSlices = this._filteredUniqueSlices;
    } else {
      this.uniqueSlices = this._uniqueSlices;
    }
  }

  private updateSeriesData() {
    if (this._excludeFalsy) {
      this.pages = this._filteredPages;
    } else {
      this.pages = this._pages;
    }
  }

  private updateResultSeriesData() {
    if (this._excludeFalsy) {
      this.resultSeries = this._filteredResultData;
    } else {
      this.resultSeries = this._resultData;
    }
  }

  private getResultsSeriesData(resultSeries, excludeFalsy): Series[] {
    return resultSeries.filter((serie: Series) =>
      excludeFalsy ? serie.metrics.some(metric => metric.values.some(value => !!value)) : true,
    );
  }

  conditionalColor(index, metric) {
    if (!this.chartOptions || !this.chartOptions.heatmap) {
      return;
    }

    const max = this.maxValues[metric.name];
    if (max !== null) {
      let metricValue;
      if (this.chartOptions.transpose === 'rows') {
        metricValue = metric.value;
      } else {
        metricValue = metric.values[index];
      }
      if (!metricValue) {
        return '#ffffff';
      }
      let v = metricValue / max;
      if (this.getComparison(metric) === 'red_increase') {
        v = 1.0 - v;
      }
      const color = this.colorScale(v);
      return color;
    }
  }

  getComparison(metric): MetricComparison {
    if (
      metric.metric &&
      metric.metric.meta &&
      metric.metric.meta.display &&
      metric.metric.meta.display.format &&
      metric.metric.meta.display.format.comparison
    ) {
      return metric.metric.meta.display.format.comparison;
    }
    return 'green_increase';
  }

  getBenchmarkClassNames(comparison: MetricComparison, value: number) {
    if (value === 0) {
      return '';
    }
    if ((value > 0 && comparison === 'green_increase') || (value < 0 && comparison === 'red_increase') ) {
      return 'benchmark__green';
    }
    return 'benchmark__red';
  }

  updateCurrentPageNum(pageNum: number) {
    if (this.currentPageNum !== pageNum) {
      this.currentPageNum = pageNum;
      this.chartOptions.initialPageNum = this.currentPageNum;
      this.calculateGrandTotals();
      this.changeDetectorRef.markForCheck();
    }
  }

  removeDuplicatedSlices(items) {
    const unique = [];
    const uniqueItems = items.filter(item => {
      const itemValues = item.map(i => i._value);
      const isDuplicate = unique.some(uniqueItem => isEqual(uniqueItem, itemValues));
      if (!isDuplicate) {
        unique.push(itemValues);
        return true;
      }
      return false;
    });
    return uniqueItems;
  }

  getNbrOfPages() {
    if (this.chartOptions.transpose === 'columns') {
      return this.uniqueSlices.length;
    } else {
      return this.pages.length;
    }
  }

  private updateBenchmarkPages(benchmarkResults) {
    from(benchmarkResults).subscribe((b: Result) => {
      if (this.chartOptions.transpose === 'columns') {
        const benchSeriesData = this.getResultsSeriesData(b.series, this._excludeFalsy);
        const benchmarksBySlices = benchSeriesData.reduce((memo, series) => {
          const key = series.slices.map(s => s.id).join('-');
          const values = series.metrics.reduce((memo, metric) => {
            memo[metric.name] = metric.values;
            return memo;
          }, {});
          memo[key] = values;
          return memo;
        }, {});
        const benchmarks = this.resultSeries.map(series => {
          const key = series.slices.map(s => s.id).join('-');
          return benchmarksBySlices[key];
        });

        if (benchmarks && benchmarks.length) {
          this.benchmarkPages = this.benchmarkService.createBenchmarkPages(this.resultSeries, benchmarks, this.resultsPerPage, 'columns');
          this.loadingBenchmarks = false;
          this.changeDetectorRef.markForCheck();
        }
      } else {
        const rowsData = this.getRowsData(this.result.rows, this._excludeFalsy);
        const benchmarksBySlices = b.rows.reduce((memo, row) => {
          const key = row.slices.map(s => s.id).join('-');
          if (!memo[key]) {
            memo[key] = {};
          }
          const date = row.date.getTime();
          const values = row.metrics.reduce((memo, metric) => {
            memo[metric.name] = metric.value;
            return memo;
          }, {});
          memo[key][date] = values;
          return memo;
        }, {});

        const resultDates = this.benchmarkService.getSortedUniqueDatesFromRows(rowsData);
        const benchmarkDates = this.benchmarkService.getSortedUniqueDatesFromRows(b.rows);
        const benchmarks = rowsData.map(row => {
          const key = row.slices.map(s => s.id).join('-');
          const dateIndex = resultDates.indexOf(row.date.getTime().toString());
          if (dateIndex === -1) {
            return undefined;
          }
          const benchmarkRowDate = benchmarkDates[dateIndex];
          return benchmarksBySlices[key] ? benchmarksBySlices[key][benchmarkRowDate] : undefined;
        });

        if (benchmarks && benchmarks.length) {
          this.benchmarkPages = this.benchmarkService.createBenchmarkPages(rowsData, benchmarks, this.resultsPerPage);
          this.loadingBenchmarks = false;
          this.changeDetectorRef.markForCheck();
        }
      }
    });
  }

  private runQuery(query: Query) {
    return this.widgetQueueProxy
      .analiticsQueueProxy(query, { cache: true, skipLoader: true }).pipe(
        map(qr => Result.parse(qr)),
      );
  }

  private intervalFromDates(dates) {
    return this.datesService.fixedQueryInterval(this.datesService.parseValue(dates, this.result.query.firstDayOfWeek ? Number(this.result.query.firstDayOfWeek) : undefined));
  }

  private getBenchmarkDates(dates: DateRangeValue) {
    if (this.compareTo.value && this.compareTo.value !== 'none') {
      return this.benchmarkService.calculateBenchmark(dates, this.compareTo.value, this.compareTo.dateRange, this.result.query.firstDayOfWeek ? Number(this.result.query.firstDayOfWeek) : undefined);
    } else {
      return null;
    }
  }

  private addTracking(changes: SimpleChanges) {
    if (changes?.options) {
      const { currentValue, previousValue } = changes?.options;

      if (previousValue?.dataOptions && currentValue?.dataOptions) {
        if (previousValue.dataOptions.excludeFalsy !== currentValue.dataOptions.excludeFalsy) {
          this.trackingService.track('Table Widget: Exclude N/A and 0 values', { value: currentValue.dataOptions.excludeFalsy });
        }
        if (previousValue.dataOptions.showColumnGrandTotals !== undefined && previousValue.dataOptions.showColumnGrandTotals !== currentValue.dataOptions.showColumnGrandTotals) {
          this.trackingService.track('Table Widget: Show column grand totals', { value: currentValue.dataOptions.showColumnGrandTotals });
        }
        if (previousValue.dataOptions.showRowGrandTotals !== undefined && previousValue.dataOptions.showRowGrandTotals !== currentValue.dataOptions.showRowGrandTotals) {
          this.trackingService.track('Table Widget: Show row grand totals', { value: currentValue.dataOptions.showRowGrandTotals });
        }
        if (previousValue.dataOptions.showColumnAllPagesTotals !== undefined && previousValue.dataOptions.showColumnAllPagesTotals !== currentValue.dataOptions.showColumnAllPagesTotals) {
          this.trackingService.track('Table Widget: Show column all pages totals', { value: currentValue.dataOptions.showColumnAllPagesTotals });
        }
      }

      if (previousValue?.footer && currentValue?.footer) {
        if (previousValue?.footer?.dateRange !== undefined && previousValue?.footer?.dateRange !== currentValue?.footer?.dateRange) {
          this.trackingService.track('Table Widget: Footer DateRange', { value: currentValue.footer.dateRange });
        }

        if (previousValue?.footer?.datasetInfo !== currentValue?.footer?.datasetInfo) {
          this.trackingService.track('Table Widget: Footer Dataset Info', { value: currentValue.footer.datasetInfo });
        }
      }

      if (previousValue?.compareTo && currentValue?.compareTo) {
        if (previousValue?.compareTo.value !== currentValue?.compareTo.value) {
          this.trackingService.track('Table Widget: Compare To', { value: currentValue.compareTo.name, dateRange: currentValue.compareTo.dateRange });
        }
      }
    }
  }
}
